const React = require('react');
const { createElement: h, useState, useEffect, useCallback } = React;
const { request, getApiOrigin } = require('../lib/apiClient');
const ConfirmDialog = require('./ConfirmDialog');
require('./ApiKeysPage.css');

function formatDate(value) {
  if (!value) return '-';
  var date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '-';
  var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return months[date.getMonth()] + ' ' + date.getDate() + ', ' + date.getFullYear();
}

function ApiKeysPage() {
  var [keys, setKeys] = useState([]);
  var [loading, setLoading] = useState(true);
  var [newKeyName, setNewKeyName] = useState('');
  var [creating, setCreating] = useState(false);
  var [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  var [copied, setCopied] = useState(false);
  var [error, setError] = useState('');
  var [revokeTarget, setRevokeTarget] = useState(null);

  var loadKeys = useCallback(function() {
    setLoading(true);
    request('/api-keys')
      .then(function(res) {
        setKeys(res && res.data ? res.data : []);
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : 'Failed to load API keys.');
      })
      .finally(function() {
        setLoading(false);
      });
  }, []);

  useEffect(function() {
    loadKeys();
  }, [loadKeys]);

  function handleCreate(e) {
    e.preventDefault();
    var name = newKeyName.trim();
    if (!name || creating) return;
    setCreating(true);
    setError('');
    setNewlyCreatedKey(null);
    request('/api-keys', { method: 'POST', body: { name: name } })
      .then(function(res) {
        setNewlyCreatedKey(res && res.data ? res.data : null);
        setNewKeyName('');
        loadKeys();
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : 'Failed to create API key.');
      })
      .finally(function() {
        setCreating(false);
      });
  }

  function confirmRevoke() {
    var target = revokeTarget;
    if (!target || !target.id) { setRevokeTarget(null); return; }
    setError('');
    var keyId = target.id;
    setRevokeTarget(null);
    request('/api-keys/' + keyId, { method: 'DELETE' })
      .then(function() {
        loadKeys();
        if (newlyCreatedKey && newlyCreatedKey.id === keyId) {
          setNewlyCreatedKey(null);
        }
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : 'Failed to revoke API key.');
      });
  }

  function handleCopy() {
    if (!newlyCreatedKey || !newlyCreatedKey.key) return;
    navigator.clipboard.writeText(newlyCreatedKey.key).then(function() {
      setCopied(true);
      setTimeout(function() { setCopied(false); }, 2000);
    }).catch(function() {});
  }

  return h('div', { className: 'akp-content' },
    h('header', { className: 'akp-hero' },
      h('p', { className: 'akp-hero__eyebrow' }, 'Settings'),
      h('h1', { className: 'akp-hero__title' }, 'API Keys'),
      h('p', { className: 'akp-hero__copy' }, 'Generate API keys to integrate Alleflow with third-party applications. Use the /v1 endpoints with your key in the X-API-Key header.')
    ),

    h('div', { className: 'akp-section' },
      h('h2', { className: 'akp-section__title' }, 'Create a new key'),
      h('form', { className: 'akp-create-form', onSubmit: handleCreate },
        h('input', {
          className: 'akp-input',
          type: 'text',
          placeholder: 'Key name (e.g. "My Integration")',
          value: newKeyName,
          onChange: function(e) { setNewKeyName(e.target.value); },
          disabled: creating
        }),
        h('button', {
          className: 'akp-create-btn',
          type: 'submit',
          disabled: creating || !newKeyName.trim()
        }, creating ? 'Creating...' : 'Generate key')
      )
    ),

    newlyCreatedKey
      ? h('div', { className: 'akp-new-key-banner' },
          h('p', { className: 'akp-new-key-banner__label' }, 'Copy your API key now. You won\'t be able to see it again.'),
          h('div', { className: 'akp-new-key-banner__row' },
            h('code', { className: 'akp-new-key-banner__key' }, newlyCreatedKey.key),
            h('button', {
              className: 'akp-copy-btn',
              type: 'button',
              onClick: handleCopy
            }, copied ? 'Copied!' : 'Copy')
          )
        )
      : null,

    error
      ? h('div', { className: 'akp-error' }, error)
      : null,

    h('div', { className: 'akp-section' },
      h('h2', { className: 'akp-section__title' }, 'Your API keys'),
      loading
        ? h('p', { className: 'akp-loading' }, 'Loading...')
        : keys.length === 0
          ? h('p', { className: 'akp-empty' }, 'No API keys yet.')
          : h('div', { className: 'akp-table-wrap' },
              h('table', { className: 'akp-table' },
                h('thead', null,
                  h('tr', null,
                    h('th', null, 'Name'),
                    h('th', null, 'Key'),
                    h('th', null, 'Created'),
                    h('th', null, 'Last used'),
                    h('th', null, '')
                  )
                ),
                h('tbody', null,
                  keys.map(function(item) {
                    return h('tr', { key: item.id },
                      h('td', { className: 'akp-table__name' }, item.name),
                      h('td', null, h('code', { className: 'akp-table__prefix' }, item.prefix + '...')),
                      h('td', null, formatDate(item.createdAt)),
                      h('td', null, item.lastUsedAt ? formatDate(item.lastUsedAt) : 'Never'),
                      h('td', null,
                        h('button', {
                          className: 'akp-revoke-btn',
                          type: 'button',
                          onClick: function() { setRevokeTarget(item); }
                        }, 'Revoke')
                      )
                    );
                  })
                )
              )
            )
    ),

    revokeTarget
      ? h(ConfirmDialog, {
          title: 'Revoke API key?',
          message: 'Revoking "' + (revokeTarget.name || 'this key') + '" immediately disables it. Any integrations using it will stop working. This cannot be undone.',
          confirmLabel: 'Revoke key',
          cancelLabel: 'Cancel',
          danger: true,
          onConfirm: confirmRevoke,
          onCancel: function() { setRevokeTarget(null); }
        })
      : null,

    h('div', { className: 'akp-section akp-docs' },
      h('h2', { className: 'akp-section__title' }, 'Quick start'),
      h('p', { className: 'akp-docs__text' }, 'Use the X-API-Key header to authenticate requests to the /v1 API:'),
      h('pre', { className: 'akp-code-block' },
        h('code', null, [
          '# List workflows',
          'curl -H "X-API-Key: YOUR_KEY" ' + (getApiOrigin() || window.location.origin) + '/v1/workflows',
          '',
          '# Create a workflow',
          'curl -X POST -H "X-API-Key: YOUR_KEY" \\',
          '  -H "Content-Type: application/json" \\',
          '  -d \'{"workflowName": "My Workflow"}\' \\',
          '  ' + (getApiOrigin() || window.location.origin) + '/v1/workflows',
          '',
          '# Get a workflow',
          'curl -H "X-API-Key: YOUR_KEY" ' + (getApiOrigin() || window.location.origin) + '/v1/workflows/:workflowId'
        ].join('\n'))
      )
    )
  );
}

module.exports = ApiKeysPage;
